<template>
  <div class="sidebar-container" :class="{ 'collapse': !sidebar.opened }">
    <div class="sidebar-logo">
      <div class="logo-icon-wrap">
        <i class="el-icon-monitor logo-icon" />
      </div>
      <transition name="sidebarLogoFade">
        <span v-if="sidebar.opened" class="logo-title">{{ siteName }}</span>
      </transition>
    </div>

    <!-- 菜单 -->
    <el-scrollbar wrap-class="scrollbar-wrapper">
      <el-menu
        :default-active="activeMenu"
        :collapse="!sidebar.opened"
        :collapse-transition="false"
        :unique-opened="false"
        mode="vertical"
        background-color="transparent"
        text-color="#606266"
        active-text-color="#13a8a8"
      >
        <sidebar-item
          v-for="route in routes"
          :key="route.path"
          :item="route"
          :base-path="route.path"
        />
      </el-menu>
    </el-scrollbar>
  </div>
</template>

<script>
import SidebarItem from './SidebarItem.vue'
import { mapGetters } from 'vuex'

export default {
  name: 'Sidebar',
  components: { SidebarItem },
  computed: {
    ...mapGetters(['sidebar', 'permission_routes', 'siteName']),
    activeMenu() {
      const { meta, path } = this.$route
      if (meta.activeMenu) return meta.activeMenu
      return path
    },
    routes() {
      return this.permission_routes.filter(r => !r.hidden)
    }
  }
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/variables.scss' as *;

.sidebar-container {
  width: 210px;
  height: 100%;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 1001;
  overflow: hidden;
  background: $sidebar-bg;
  border-right: 1px solid $sidebar-border;
  transition: width 0.3s;

  &.collapse {
    width: 64px;

    ::v-deep .el-menu {
      width: 64px !important;
      overflow: hidden;
    }
    ::v-deep .el-submenu .el-menu-item {
      min-width: 64px !important;
    }
    ::v-deep .el-menu-item span,
    ::v-deep .el-submenu__title span {
      display: none !important;
      width: 0 !important;
      overflow: hidden !important;
    }
    ::v-deep .el-submenu__icon-arrow {
      display: none !important;
    }
    /* 收起时：取消左右 margin，图标在 64px 内居中 */
    ::v-deep .el-menu-item,
    ::v-deep .el-submenu__title {
      margin: 3px 0 !important;
      width: 64px !important;
      padding: 0 !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
    }

    .logo-title { display: none; }
  }

  /* 去除右侧边框 */
  ::v-deep .el-menu {
    border-right: none !important;
    background: transparent !important;
  }

  /* 图标间距 */
  ::v-deep .el-submenu [class^=el-icon-],
  ::v-deep .el-menu-item [class^=el-icon-] {
    margin-right: 0 !important;
  }

  /* 滚动区高度 */
  ::v-deep .el-scrollbar {
    height: calc(100vh - 60px);
  }
  ::v-deep .el-scrollbar__wrap {
    overflow-x: hidden !important;
    overflow-y: auto !important;
  }
  ::v-deep .el-scrollbar__bar.is-horizontal {
    display: none !important;
  }
  ::v-deep .el-scrollbar__bar.is-vertical {
    opacity: 0;
    transition: opacity 0.3s;
    right: 2px;
    width: 3px;
  }
  &:hover ::v-deep .el-scrollbar__bar.is-vertical {
    opacity: 1;
  }

  /* 菜单项样式 */
  ::v-deep .el-menu-item,
  ::v-deep .el-submenu__title {
    height: 46px;
    line-height: 46px;
    margin: 3px 10px;
    border-radius: 8px;
    width: calc(100% - 20px) !important;
    transition: all 0.22s ease !important;
    position: relative;
    overflow: hidden;

    &:hover {
      background: $sidebar-hover-bg !important;
      color: $sidebar-text-active !important;
    }
  }

  ::v-deep .el-menu-item.is-active {
    background: $sidebar-active-bg !important;
    color: $sidebar-text-active !important;
    font-weight: 600;
  }

  ::v-deep .el-submenu__title {
    &:hover { background: $sidebar-hover-bg !important; }
  }

  ::v-deep .el-submenu .el-menu {
    background: #f8fafc !important;
    border-radius: 0 0 8px 8px;
    margin: 0 8px;
    width: calc(100% - 16px) !important;
    padding-bottom: 4px;
  }

  /* 子菜单项缩进 */
  ::v-deep .el-submenu .el-menu-item {
    padding-left: 44px !important;
    margin: 2px 0 !important;
    width: 100% !important;
    border-radius: 6px;
  }
}

.sidebar-logo {
  height: 60px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  border-bottom: 1px solid $sidebar-border;
  background: $sidebar-bg;

  .logo-icon-wrap {
    width: 34px;
    height: 34px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: $theme-primary;
    border-radius: 10px;

    .logo-icon {
      font-size: 17px;
      color: #fff;
    }
  }

  .logo-title {
    margin-left: 12px;
    color: #303133;
    font-size: 15px;
    font-weight: 700;
    white-space: nowrap;
    letter-spacing: 0.5px;
  }
}

.sidebarLogoFade-enter-active {
  transition: opacity 0.15s 0.2s;
}
.sidebarLogoFade-enter,
.sidebarLogoFade-leave-to {
  opacity: 0;
}
</style>

<style lang="scss">
@use '@/assets/styles/variables.scss' as *;

.el-menu--popup-container .el-menu--popup,
.el-menu--popup {
  background: $sidebar-popup-bg !important;
  border: 1px solid $sidebar-border !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08) !important;
  border-radius: 8px !important;
  padding: 4px !important;
  min-width: 140px !important;

  .el-menu-item {
    height: 42px !important;
    line-height: 42px !important;
    border-radius: 6px !important;
    color: $sidebar-text !important;
    margin: 2px 0 !important;

    &:hover {
      background: $sidebar-hover-bg !important;
      color: $sidebar-text-active !important;
    }

    &.is-active {
      background: $sidebar-active-bg !important;
      color: $sidebar-text-active !important;
      font-weight: 600 !important;
    }
  }
}
</style>
